import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { RevertIcon } from "@icons";
import {
    isMarkAsFinancialEligible,
    isUnmarkAsFinancialEligible,
} from "./accountMappingBulkEligibility";
import { AccountRow } from "./types";
import styles from "./MapImportedAccountsSpreadsheet.module.scss";

interface AccountMappingActionProps {
    row: AccountRow;
    onCreateNewAccount?: (row: AccountRow) => void;
    onUndoCreateNewAccount?: (importedAccountId: string) => void;
    onUndoMapping?: (importedAccountId: string) => void;
    onMarkAsFinancial?: (row: AccountRow) => void;
    onUnmarkAsFinancial?: (importedAccountId: string) => void;
}

interface UnmappedAccountActionButtonProps {
    readonly label: string;
    readonly onClick: () => void;
}

const UnmappedAccountActionButton: React.FC<
    UnmappedAccountActionButtonProps
> = ({ label, onClick }) => (
    <Button
        variant="primary"
        size="sm"
        onClick={onClick}
        className={styles.createAccountButton}
    >
        {label}
    </Button>
);

interface RevertActionButtonProps {
    readonly onClick: () => void;
}

const RevertActionButton: React.FC<RevertActionButtonProps> = ({ onClick }) => (
    <Button
        variant="secondary"
        size="sm"
        onClick={onClick}
        iconOnly
        aria-label="Revert mapping"
    >
        <RevertIcon />
    </Button>
);

export const AccountMappingAction: React.FC<AccountMappingActionProps> = ({
    row,
    onCreateNewAccount,
    onUndoCreateNewAccount,
    onUndoMapping,
    onMarkAsFinancial,
    onUnmarkAsFinancial,
}) => {
    if (
        row.accountConnectionTaskId &&
        row.kickAccountId &&
        !row.createNewAccount
    ) {
        return (
            <Button
                variant="primary"
                size="sm"
                onClick={() => onUndoMapping?.(row._id)}
                className={styles.undoMappingButton}
            >
                Connect account
            </Button>
        );
    }

    if (row.accountConnectionTaskId) {
        return null;
    }

    if (row.createNewAccount) {
        return (
            <RevertActionButton
                onClick={() => onUndoCreateNewAccount?.(row._id)}
            />
        );
    }

    if (isUnmarkAsFinancialEligible(row)) {
        return (
            <RevertActionButton
                onClick={() => onUnmarkAsFinancial?.(row._id)}
            />
        );
    }

    return (
        <HStack gap={12}>
            <UnmappedAccountActionButton
                label="Create account"
                onClick={() => onCreateNewAccount?.(row)}
            />
            {isMarkAsFinancialEligible(row) && (
                <UnmappedAccountActionButton
                    label="Mark as financial"
                    onClick={() => onMarkAsFinancial?.(row)}
                />
            )}
        </HStack>
    );
};
